import React from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { SelectItem } from '@mentimeter/ragnar-ui/input/select-item-legacy';
import { Text } from '@mentimeter/ragnar-ui/text';
import type { DSC } from '@mentimeter/ragnar-dsc';
import { Link } from '@mentimeter/next-navigation';
import type { FlagT, FlagVariantT } from '../../types';

const DOT_SIZE = 8;

const OPTION_DEFAULT_VALUE = 'option-default-value';

export function Flag({
  flag,
  onChange,
  onReset,
}: {
  flag: FlagT;
  onChange: (newValue: string) => void;
  onReset: () => void;
}) {
  return (
    <Box
      key={flag.key}
      className="derp"
      width="100%"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      display="grid"
      p="space2"
      borderRadius="xl"
      gap="space2"
      gridTemplateColumns="2fr 1fr"
    >
      <Box>
        <Box flexDirection="row" alignItems="center" gap="space2">
          <Text
            fontWeight={flag.active ? 'bold' : 'semiBold'}
            color={flag.active ? 'text' : 'textWeaker'}
            extend={() => ({
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            })}
          >
            {flag.displayName}
          </Text>

          {flag.active && (
            <Box
              extend={({ theme }) => ({
                backgroundColor: getBubbleColor(flag.type, theme),
                width: `${DOT_SIZE}px`,
                height: `${DOT_SIZE}px`,
                borderRadius: `${DOT_SIZE}px`,
              })}
            />
          )}
        </Box>

        {flag.description && (
          <Text fontSize="87.5" color="textWeaker">
            {flag.description}
          </Text>
        )}

        {flag.displayKey && flag.type === 'confidence' && flag.externalLink && (
          <Link
            mt="space0.5"
            href={flag.externalLink}
            target="_blank"
            fontSize="87.5"
            color="textWeaker"
          >
            #{flag.displayKey}
          </Link>
        )}
      </Box>

      <Box>
        <SelectItem
          key={flag.value}
          value={flag.value}
          inputSize="compact"
          data-testid={`flag-select-item-${flag.key}`}
          onChange={(e) => {
            if (e.target.value === OPTION_DEFAULT_VALUE) {
              onReset();
            } else {
              onChange(e.target.value);
            }
          }}
        >
          <SelectItem.Option value={OPTION_DEFAULT_VALUE}>
            default
          </SelectItem.Option>

          {flag.options?.map((option) => (
            <SelectItem.Option key={option}>{option}</SelectItem.Option>
          ))}
        </SelectItem>
      </Box>
    </Box>
  );
}

function getBubbleColor(variant: FlagVariantT, theme: DSC) {
  switch (variant) {
    case 'development':
      return theme.colors.notice;
    case 'confidence':
      return theme.colors.positive;
    default:
      return theme.colors.text;
  }
}
